import { useCallback, useEffect } from 'react';

import { type Event, Fit, Layout, type RiveEventPayload } from '@rive-app/react-canvas';
import { createPortal } from 'react-dom';

import xpGiftRive from '../../../assets/missions/xp_gift.riv';
import { useRiveMachine } from '../../../hooks/rive/useRiveMachine';
import { fireTrigger, setNumber } from '../../../utils/riveHelper';

import classes from './MissionXpGiftAnimation.module.scss';

const STATE_MACHINE_NAME = 'State Machine 1';

interface Props {
  boxNum: number;
  onBoxOpen?: () => void;
  onBoxFinish?: () => void;
}

export function MissionXpGiftAnimation({ boxNum, onBoxOpen, onBoxFinish }: Props) {
  const handleName = useCallback(
    (raw?: string) => {
      const name = raw?.toLowerCase().replace(/[\s_]/g, '');
      if (name === 'boxopen') onBoxOpen?.();
      if (name === 'boxfinish') onBoxFinish?.();
    },
    [onBoxOpen, onBoxFinish],
  );

  const onRiveEvent = useCallback(
    (event: Event) => {
      if (import.meta.env.DEV) console.log('[xpGift] riveEvent', event?.data);
      handleName((event?.data as RiveEventPayload)?.name);
    },
    [handleName],
  );

  const onStateChange = useCallback(
    (event: Event) => {
      if (import.meta.env.DEV) console.log('[xpGift] stateChange', event?.data);
      const states = (event?.data as string[]) ?? [];
      states.forEach(handleName);
    },
    [handleName],
  );

  const { rive, RiveComponent } = useRiveMachine({
    src: xpGiftRive,
    stateMachineName: STATE_MACHINE_NAME,
    layout: new Layout({ fit: Fit.Contain }),
    autoplay: true,
    autoBind: true,
    onRiveEvent,
    onStateChange,
  });

  useEffect(() => {
    const vm = rive?.viewModelInstance;
    if (!vm) return;
    setNumber(vm, 'boxNum', boxNum);
    fireTrigger(vm, 'claimGift');
  }, [rive, boxNum]);

  return createPortal(
    <div className={classes.animation}>
      <RiveComponent />
    </div>,
    document.body,
  );
}
